"use client"

import { ToolShareBar } from "@/components/brand-kit/tool-share-bar"

interface LocalShareButtonsProps {
  locationName: string
  pageUrl?: string
  /** No-op since the editorial redesign — legacy "top" / "bottom" are visually identical now. */
  variant?: "top" | "bottom"
  fisScore?: number
  electricityRate?: number
  /** Rebate amounts must never be presented as an electricity rate. */
  pageType?: "report" | "rebate"
}

/**
 * LocalShareButtons — thin wrapper that delegates to `ToolShareBar`.
 *
 * Pre-redesign this component owned its own multi-network rail (Instagram,
 * Pinterest, Facebook, X, Email, Copy, Google) with a branded toast. After
 * the editorial redesign the entire site uses `ToolShareBar` (X, Facebook,
 * LinkedIn, Copy, Email, Gmail) so every state / city / ZIP / rebate page
 * shares the same compact share row that legal, tool, and blog pages do.
 *
 * The location-aware `fisScore` + `electricityRate` props are still consumed
 * here — they're folded into the share title so the resulting tweet / mail
 * subject still carries the local context.
 */
export function LocalShareButtons({
  locationName,
  pageUrl,
  fisScore,
  electricityRate,
  pageType = "report",
}: LocalShareButtonsProps) {
  const parts: string[] = [locationName]
  if (typeof fisScore === "number" && fisScore > 0) parts.push(`FIS ${fisScore}`)
  if (pageType === "report" && typeof electricityRate === "number" && electricityRate > 0) {
    parts.push(`${electricityRate.toFixed(1)}¢/kWh`)
  }
  const title = parts.join(" · ")
  // FRIDGE-352: every button's tooltip / accessible name says WHAT is shared
  // and carries the brand — "Share Austin, TX on X — Fridge.com" — so the
  // subject is the place name alone, not the FIS / rate share copy.
  return <ToolShareBar title={title} url={pageUrl} subject={locationName} />
}
